import { Downgraded, useHookstate } from "@hookstate/core";
import { useEffect } from "react";
import { createDefaultCommander, createDefaultDriver, createDefaultErrorState } from "~/features/Device/TabListDevice/components/ConectDispatchVehicle/constants";
import type { IErrorConectDispatchVehicle, IUserCrew } from "~/features/Device/TabListDevice/interfaces/IListDevicesprops";
import editWorkSchedule, { checkAvailability, createWorkSchedule } from "../services";
import type IUser from "~/features/User/interfaces/IUser";
import type { CheckAvailabilityParams, CheckAvailabilityResponse, CrewList, DisabledNextButton, handleCreateAndEditWorkScheduleParams } from "../interfaces";
import type { WorkShiftTypeResponse } from "~/components/FilterAutocompleteWorkShiftType/interfaces";
import dayjs from "dayjs";
import type IDevice from "~/features/Device/interfaces/IDevice";
import type { OptionType } from "~/components/SelectBasic";
import notify from "~/utils/notify";
import { useLang } from "~/hooks/useLang";
import { useHistory, useLocation } from "react-router-dom";

const POSITIONS = {
  COMANDANTE: 1,
  MOTORISTA: 2,
  PATRULHEIRO: 3
} as const;

const disabledNextButtonDefault: DisabledNextButton = {
  disabledWorkShift: true,
  disabledDevice: true,
  disabledCrew: true,
  availableDevice: false,
  availableUser: false,
};

// eslint-disable-next-line max-lines-per-function
export function useWorkScheduleForm() {
  const { translate } = useLang();
  const history = useHistory()
  const startDate = useHookstate<string | null>(null);
  const endDate = useHookstate<string | null>(null);
  const userId = useHookstate<number | null>(null);
  const crewPositionId = useHookstate<string | null>(null);
  const workShift = useHookstate<WorkShiftTypeResponse | null>(null);
  const device = useHookstate<IDevice | null>(null);
  const commander = useHookstate<IUserCrew>(createDefaultCommander());
  const driver = useHookstate<IUserCrew>(createDefaultDriver());
  const task = useHookstate<string | null>(null);
  const patrolmans = useHookstate<ReadonlyArray<IUserCrew>>([]);
  const error = useHookstate<IErrorConectDispatchVehicle>(createDefaultErrorState());
  const initialPeriodCreated = useHookstate<string>('');
  const resetValues = useHookstate(false);
  const focusChange = useHookstate<boolean>(false);
  const crewList = useHookstate<CrewList[]>([]);
  const activeStep = useHookstate<number>(0);
  const disabledNextButton = useHookstate<DisabledNextButton>(disabledNextButtonDefault);
  const checkAvailabilityResult = useHookstate<CheckAvailabilityResponse | null>(null);
  const initialCrewUserIds = useHookstate<number[]>([]);

  const crewPositionOptions = useHookstate([
    { id: POSITIONS.COMANDANTE, name: 'Comandante' },
    { id: POSITIONS.MOTORISTA, name: 'Motorista' },
    { id: POSITIONS.PATRULHEIRO, name: 'Patrulheiro' },
  ])

  const requiredFields = useHookstate({
    km: false,
    phone: false,
    enableIntegrationSystemRh: false,
    upLoadImage: false,
    deviceSituation: false
  });

  useEffect(() => {
    const deviceData = device.get();
    crewPositionOptions.set([
      { id: POSITIONS.COMANDANTE, name: deviceData?.device_type?.label_commander ?? 'Comandante' },
      { id: POSITIONS.MOTORISTA, name: deviceData?.device_type?.label_driver ?? 'Motorista' },
      { id: POSITIONS.PATRULHEIRO, name: deviceData?.device_type?.label_patrolmans ?? 'Patrulheiro' },
    ]);
  }, [device.get()?.id]);

  useEffect(() => {
    checkAvailabilityDeviceAndUser();
  }, [device.get()?.id, userId.get()]);


  const handleCancelButton = () => {
    history.push('/work-schedule')
  }

  const clearMessageError = () => {
    activeStep.get() === 1 && !device.get()?.id ? checkAvailabilityResult.merge({ error: null }) : null;
    activeStep.get() === 2 && !userId.get() ? checkAvailabilityResult.merge({ error: null }) : null;
  }


  const checkAvailabilityDeviceAndUser = () => {
    clearMessageError();

    const deviceData = Boolean(activeStep.get() === 1 && device.get()?.id);
    const userData = Boolean(activeStep.get() === 2 && userId.get());

    if (!deviceData && !userData) {
      checkAvailabilityResult.merge({ error: null });
      return;
    }

    const currentUserId = userId.get();
    const isInitialUser = currentUserId && initialCrewUserIds.get().includes(currentUserId);

    if (userData && isInitialUser) {
      checkAvailabilityResult.merge({ error: null });
      disabledNextButton.merge({ availableUser: true });
      return;
    }

    const params: CheckAvailabilityParams = {
      deviceId: deviceData ? device.get()?.id : null,
      userId: userData ? userId.get() : null,
      startDate: String(startDate.get()),
      endDate: String(endDate.get()),
    }

    if (startDate.get() && endDate.get()) {
      checkAvailability(params)
        .then((response: CheckAvailabilityResponse) => {
          checkAvailabilityResult.set(response);

          const existError = Boolean(checkAvailabilityResult.get()?.error);

          if (!existError) {
            activeStep.get() === 1 ?
              disabledNextButton.merge({ availableDevice: true }) :
              disabledNextButton.merge({ availableUser: true });
          } else {
            activeStep.get() === 1 ?
              disabledNextButton.merge({ availableDevice: false }) :
              disabledNextButton.merge({ availableUser: false });
          }

        });
    }
  }

  const verifyRequiredFieldsCreateWorkSchedule = () => {
    if (workShift.get() && startDate.get() && endDate.get() && commander.get()) {
      return true
    }
  }

  function defineEndDateRestHours(startDate) {
    const initialDate = dayjs(startDate);
    const setfutureEndDate = initialDate.add(Number(workShift.get()?.work_hours), 'hour');
    startDate.set(startDate.toJSON());
    endDate.set(setfutureEndDate.toJSON());
  }

  // Funções auxiliares para validação
  const validateUserAndPosition = (userId: number, positionId: number): boolean => {
    if (!userId || !positionId) {
      notify({
        type: 'warning',
        message: translate('Please select user and position')
      });
      return false;
    }
    return true;
  };

  const getUserPositions = (userId: number): number[] => {
    return crewList.get()
      .filter(member => member.id === userId)
      .map(member => Number(member.position.id));
  };

  const isPositionOccupied = (positionId: number): boolean => {
    if (positionId === POSITIONS.PATRULHEIRO) return false;

    const occupied = crewList.get().some(member => member.position.id === positionId);

    if (occupied) {
      const positionName = crewPositionOptions.get().find(opt => opt.id === positionId)?.name ?? '';
      notify({
        type: 'error',
        message: translate('Position of positionName is already occupied', { positionName: positionName })
      });
    }

    return occupied;
  };

  const canBeCommanderAndDriver = (userPositions: number[], selectedPositionId: number): boolean => {
    const hasCommander = userPositions.includes(POSITIONS.COMANDANTE);
    const hasDriver = userPositions.includes(POSITIONS.MOTORISTA);

    // Se já tem ambas, não pode adicionar mais
    if (hasCommander && hasDriver) {
      notify({
        type: 'error',
        message: translate('User is already registered as Commander and Driver')
      });
      return false;
    }

    // Verifica se está tentando adicionar uma posição que já tem
    if (userPositions.includes(selectedPositionId)) {
      const positionName = crewPositionOptions.get().find(opt => opt.id === selectedPositionId)?.name ?? '';
      notify({
        type: 'error',
        message: translate('User is already registered as', { positionName: positionName })
      });
      return false;
    }

    // Permite se for comandante OU motorista (não ambos)
    const isCommanderOrDriver =
      selectedPositionId === POSITIONS.COMANDANTE ||
      selectedPositionId === POSITIONS.MOTORISTA;

    return isCommanderOrDriver && (hasCommander || hasDriver);
  };

  const canAddPatrolman = (userPositions: number[], selectedPositionId: number): boolean => {
    const isPatrolman = selectedPositionId === POSITIONS.PATRULHEIRO;
    const hasOtherPosition = userPositions.some(pos =>
      pos === POSITIONS.COMANDANTE || pos === POSITIONS.MOTORISTA
    );
    const isAlreadyPatrolman = userPositions.includes(POSITIONS.PATRULHEIRO);

    if (isPatrolman && hasOtherPosition) {
      notify({
        type: 'error',
        message: translate('User cannot be Patrolman and have another role simultaneously')
      });
      return false;
    }

    if (!isPatrolman && isAlreadyPatrolman) {
      notify({
        type: 'error',
        message: translate('User cannot be Patrolman and have another role simultaneously')
      });
      return false;
    }

    if (isPatrolman && isAlreadyPatrolman) {
      notify({
        type: 'error',
        message: translate('User is already registered as Patrolman')
      });
      return false;
    }

    return true;
  };

  const addMemberToCrew = (user: IUser, position: OptionType) => {
    crewList.merge([{
      id: Number(user.id),
      position: { id: position.id, name: position.name } as OptionType,
      representationName: user?.representation_name ? String(user.representation_name) : String(user.name),
      registration: Number(user.registration),
      name: String(user.name)
    }]);
  };

  const addCrewMember = (user: IUser, position: OptionType): boolean => {
    const selectedUserId = Number(user?.id);
    const selectedPositionId = Number(position?.id);

    // Validação inicial
    if (!validateUserAndPosition(selectedUserId, selectedPositionId)) {
      return false;
    }

    const userPositions = getUserPositions(selectedUserId);
    const isNewUser = userPositions.length === 0;


    // Se é um novo usuário
    if (isNewUser) {
      if (isPositionOccupied(selectedPositionId)) {
        return false;
      }
      addMemberToCrew(user, position);
      return true;
    }

    // Se usuário já existe, valida se pode adicionar nova posição

    // Caso especial: Comandante e Motorista podem ser a mesma pessoa
    const isCommanderOrDriver =
      selectedPositionId === POSITIONS.COMANDANTE ||
      selectedPositionId === POSITIONS.MOTORISTA;

    const hasCommanderOrDriver =
      userPositions.includes(POSITIONS.COMANDANTE) ||
      userPositions.includes(POSITIONS.MOTORISTA);

    if (isCommanderOrDriver && hasCommanderOrDriver) {
      if (canBeCommanderAndDriver(userPositions, selectedPositionId)) {
        addMemberToCrew(user, position);
        return true;
      }
      return false;
    }

    // Validação de Patrulheiro
    if (!canAddPatrolman(userPositions, selectedPositionId)) {
      return false;
    }

    // Se chegou aqui, não deveria permitir (usuário já tem posição incompatível)
    notify({
      type: 'error',
      message: translate('User is already registered in this position')
    });
    return false;
  }

  const removeCrewMember = (memberId: number) => {
    const currentList = crewList.get();
    const filteredList = currentList.filter(member => member.id !== memberId);
    crewList.set(filteredList);
  }

  function handleCreateAndEditWorkSchedule({ isCreate = true, id }: handleCreateAndEditWorkScheduleParams) {
    if (verifyRequiredFieldsCreateWorkSchedule()) {
      const crew: { user_id: number, crew_position_id: number }[] = crewList.get().map(member => ({
        user_id: member.id,
        crew_position_id: Number(member.position.id)
      }))

      if (isCreate) {
        createWorkSchedule({
          workShiftId: Number(workShift.get()?.id),
          startDate: String(startDate.get()),
          endDate: String(endDate.get()),
          deviceId: Number(device.get()?.id),
          task: task.get(),
          crew
        }).finally(() => {
          history.push('/work-schedule')
        })
      } else {
        editWorkSchedule({
          id: Number(id),
          workShiftId: Number(workShift.get()?.id),
          startDate: String(startDate.get()),
          endDate: String(endDate.get()),
          deviceId: Number(device.get()?.id),
          task: task.get(),
          crew
        }).finally(() => {
          history.push('/work-schedule')
        })
      }
    }
  }

  function onPeriodChangeCreated(value) {
    initialPeriodCreated.set(value)
  }

  const resetForm = () => {
    startDate.set(null);
    endDate.set(null);
    userId.set(null);
    crewPositionId.set(null);
    workShift.set(null);
    device.set(null);
    commander.set(createDefaultCommander());
    driver.set(createDefaultDriver());
    task.set(null);
    patrolmans.set([]);
    error.set(createDefaultErrorState());
    crewList.set([]);
  };

  const handleNext = () => {
    activeStep.set((prevActiveStep) => prevActiveStep + 1);
    activeStep.get() === 1 || activeStep.get() === 2 ? checkAvailabilityDeviceAndUser() : null
  };

  const handleBack = () => {
    activeStep.get() === 1 || activeStep.get() === 2 ? checkAvailabilityResult.merge({ error: null }) : null
    activeStep.set((prevActiveStep) => prevActiveStep - 1);
  };

  const handleReset = () => {
    activeStep.set(0);
  };

  useEffect(() => {
    verifyDisabledNextButton();
  }, [
    workShift.get()?.id,
    startDate.value,
    endDate.value,
    device.get()?.id,
    crewList.value.length,
    activeStep.value,
    disabledNextButton.get().availableDevice,
    disabledNextButton.get().availableUser
  ]);

  const verifyDisabledNextButton = () => {
    const currentStep = activeStep.get();

    // Validação do Step 0: Turno e Data
    if (currentStep === 0) {
      disabledNextButton.merge({ disabledWorkShift: true });
      const hasWorkShift = Boolean(workShift.get());
      const hasStartDate = Boolean(startDate.get());
      const hasEndDate = Boolean(endDate.get());

      const isStepValid = hasWorkShift && hasStartDate && hasEndDate;
      if (isStepValid) {
        disabledNextButton.merge({ disabledWorkShift: false });
      } else {
        disabledNextButton.merge({ disabledWorkShift: true });
      }
    }

    // Validação do Step 1: Dispositivo
    if (currentStep === 1) {
      disabledNextButton.merge({ disabledDevice: true });
      const hasDevice = Boolean(device.get());
      const deviceAvailable = disabledNextButton.get().availableDevice;
      if (hasDevice && deviceAvailable) {
        disabledNextButton.merge({ disabledDevice: false });
      } else {
        disabledNextButton.merge({ disabledDevice: true });
      }
    }

    // Validação do Step 2: Equipe (Comandante obrigatório)
    if (currentStep === 2) {
      disabledNextButton.merge({ disabledCrew: true });
      const hasCommander = crewList.get().some(
        crew => crew.position?.id === POSITIONS.COMANDANTE
      );
      if (hasCommander) {
        disabledNextButton.merge({ disabledCrew: false });
      } else {
        disabledNextButton.merge({ disabledCrew: true });
      }
    }
  }

  return {
    startDate,
    endDate,
    userId,
    crewPositionId,
    initialPeriodCreated,
    workShift,
    device,
    commander,
    driver,
    task,
    patrolmans,
    error,
    requiredFields,
    resetValues,
    focusChange,
    crewList,
    crewPositionOptions,
    activeStep,
    disabledNextButton,
    checkAvailabilityResult,
    initialCrewUserIds,
    // functions
    handleCancelButton,
    resetForm,
    onPeriodChangeCreated,
    defineEndDateRestHours,
    addCrewMember,
    removeCrewMember,
    handleCreateAndEditWorkSchedule,
    handleNext,
    handleBack,
    handleReset,
    verifyDisabledNextButton,
  };
}
